import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/api";

const HSV_OPTIONS = ["HSV-1", "HSV-2", "Both", "Private"];
const GENDER_OPTIONS = ["Woman", "Man", "Non-binary", "Prefer not to say"];
const INTERESTED_IN = ["Women", "Men", "Non-binary", "Everyone"];
const LOOKING_FOR = ["Relationship", "Dating", "Friendship", "Casual", "Community/Support"];
const SUGGESTED_INTERESTS = ["Books", "Hiking", "Yoga", "Cooking", "Art", "Music", "Travel", "Coffee", "Photography", "Plants", "Gaming", "Dogs"];

function MultiSelectChips({ value = [], options, onChange, testidPrefix }) {
  const toggle = (o) => {
    if (value.includes(o)) onChange(value.filter((v) => v !== o));
    else onChange([...value, o]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)}
            data-testid={`${testidPrefix}-${o.replace(/\s|\//g, "-")}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              active
                ? "bg-[#FFF0EC] border-[#D95D39] text-[#C24C29]"
                : "bg-white border-[#E8DEC8] text-[#2C2623] hover:bg-[#F9F5EE]"
            }`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [interestInput, setInterestInput] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) setForm({
      age: user.age || "",
      location: user.location || "",
      bio: user.bio || "",
      hsv_status: user.hsv_status || "",
      interests: user.interests || [],
      gender: user.gender || "",
      interested_in: user.interested_in || [],
      looking_for: user.looking_for || [],
    });
  }, [user]);

  const save = async () => {
    if (form.age && parseInt(form.age) < 18) {
      toast.error("HerpMates is strictly 18+. Age must be 18 or older.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        age: form.age ? parseInt(form.age) : null,
        location: form.location,
        bio: form.bio,
        hsv_status: form.hsv_status || null,
        interests: form.interests,
        gender: form.gender || null,
        interested_in: form.interested_in,
        looking_for: form.looking_for,
      };
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success("Profile saved");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const addInterest = (v) => {
    const val = v.trim();
    if (!val || form.interests?.includes(val)) return;
    setForm((f) => ({ ...f, interests: [...(f.interests || []), val] }));
    setInterestInput("");
  };
  const removeInterest = (i) => setForm((f) => ({ ...f, interests: f.interests.filter((x) => x !== i) }));

  // Memoize the suggested-interests filter so it isn't recomputed on every render.
  const suggestedInterestsAvailable = useMemo(() => {
    const chosen = new Set(form.interests || []);
    return SUGGESTED_INTERESTS.filter((s) => !chosen.has(s));
  }, [form.interests]);

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (path) => {
    try {
      await api.delete(`/profile/photo?path=${encodeURIComponent(path)}`);
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Photo removed");
    } catch {
      toast.error("Couldn't remove photo");
    }
  };

  const photoUrl = (p) => `${BACKEND_URL}/api/files/${p}`;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623]">Your profile</h1>
          <p className="text-[#6E655F] mt-2">Show up as your beautiful, honest self.</p>
        </div>

        <Card className="p-6 rounded-3xl border-[#E8DEC8] bg-white">
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="w-20 h-20 ring-4 ring-[#FFF0EC]">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-[#D95D39] text-white text-2xl">{user.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="font-serif text-2xl font-semibold text-[#2C2623] flex items-center gap-2 flex-wrap">
                {user.name}
                {user.is_founding_member && (
                  <span
                    data-testid="profile-founding-badge"
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-[#E09F3E] to-[#D95D39] text-white text-xs font-bold uppercase tracking-wider shadow-sm">
                    ✨ Founding Member
                  </span>
                )}
                {user.is_identity_verified && (
                  <span
                    data-testid="profile-identity-verified-badge"
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#EAF3F0] text-[#2A524A] text-xs font-bold uppercase tracking-wider border border-[#B7D4C7]">
                    ✓ Verified
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {user.email_verified ? (
                  <span data-testid="email-verified-pill"
                    className="inline-flex items-center gap-1 text-xs text-[#2A524A] bg-[#EAF3F0] px-2 py-0.5 rounded-full">
                    ✓ Email verified
                  </span>
                ) : (
                  <Link to="/verify-email" data-testid="email-verify-link"
                    className="inline-flex items-center gap-1 text-xs text-[#B45309] bg-[#FEF3D6] px-2 py-0.5 rounded-full hover:bg-[#F5D77E]">
                    ✉️ Verify email →
                  </Link>
                )}
                <span className="text-xs text-[#6E655F] italic">Your email stays private — never shown on your profile.</span>
              </div>
              {!user.is_identity_verified && (
                <div className="mt-1 text-[11px] text-[#6E655F]" data-testid="identity-verification-coming-soon">
                  Optional identity verification is coming soon — you'll be able to earn a ✓ Verified badge.
                </div>
              )}
            </div>
          </div>

          {/* Photo gallery */}
          <div className="space-y-2 mb-6">
            <Label>Photos <span className="text-xs text-[#6E655F]">(add as many as you'd like)</span></Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {(user.photos || []).map((p) => (
                <div key={p} className="relative aspect-square rounded-2xl overflow-hidden border border-[#E8DEC8] group"
                  data-testid={`profile-photo-${p.split('/').pop()}`}>
                  <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(p)}
                    data-testid="profile-photo-remove-btn"
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded-2xl border-2 border-dashed border-[#E8DEC8] flex flex-col items-center justify-center text-[#6E655F] hover:bg-[#F9F5EE] cursor-pointer">
                <input type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" data-testid="profile-photo-upload" />
                {uploading ? (
                  <span className="text-xs">Uploading…</span>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mb-1" />
                    <span className="text-xs">Add photos</span>
                  </>
                )}
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Age <span className="text-xs text-[#6E655F]">(18+ only)</span></Label>
              <Input type="number" value={form.age || ""} min={18} max={99}
                onChange={(e) => setForm({ ...form, age: e.target.value })}
                data-testid="profile-age-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input value={form.location || ""}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="profile-location-input" placeholder="City, State" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="profile-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {GENDER_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 mt-4">
            <Label>Interested in</Label>
            <MultiSelectChips
              value={form.interested_in || []}
              options={INTERESTED_IN}
              onChange={(v) => setForm({ ...form, interested_in: v })}
              testidPrefix="interested-in"
            />
          </div>

          <div className="space-y-2 mt-4">
            <Label>What are you looking for? <span className="text-xs text-[#6E655F]">(pick any)</span></Label>
            <MultiSelectChips
              value={form.looking_for || []}
              options={LOOKING_FOR}
              onChange={(v) => setForm({ ...form, looking_for: v })}
              testidPrefix="looking-for"
            />
          </div>

          <div className="space-y-1.5 mt-4">
            <Label>Bio</Label>
            <Textarea value={form.bio || ""} rows={4}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="profile-bio-input"
              placeholder="Tell folks what makes you, you." />
          </div>

          <div className="space-y-2 mt-4">
            <Label>HSV status <span className="text-xs text-[#6E655F]">(shared respectfully on your profile)</span></Label>
            <RadioGroup value={form.hsv_status || ""} onValueChange={(v) => setForm({ ...form, hsv_status: v })}
              data-testid="profile-hsv-selector"
              className="flex flex-wrap gap-3">
              {HSV_OPTIONS.map((o) => (
                <label key={o} htmlFor={`hsv-${o}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer ${
                    form.hsv_status === o
                      ? "bg-[#FFF0EC] border-[#D95D39] text-[#C24C29]"
                      : "bg-white border-[#E8DEC8] text-[#2C2623]"
                  }`}>
                  <RadioGroupItem id={`hsv-${o}`} value={o} data-testid={`hsv-option-${o}`} />
                  <span className="text-sm font-medium">{o}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2 mt-4">
            <Label>Interests</Label>
            <div className="flex flex-wrap gap-2">
              {form.interests?.map((i) => (
                <Badge key={i} onClick={() => removeInterest(i)}
                  data-testid={`interest-tag-${i}`}
                  className="bg-[#F9F5EE] text-[#2C2623] border border-[#E8DEC8] cursor-pointer hover:bg-[#FFF0EC]">
                  {i} ✕
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {suggestedInterestsAvailable.map((s) => (
                <button key={s} onClick={() => addInterest(s)}
                  data-testid={`suggested-interest-${s}`}
                  className="text-xs px-3 py-1 rounded-full bg-white border border-[#E8DEC8] text-[#6E655F] hover:bg-[#F9F5EE]">
                  + {s}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={interestInput} onChange={(e) => setInterestInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addInterest(interestInput))}
                placeholder="Add your own…" data-testid="interest-input" />
              <Button variant="outline" onClick={() => addInterest(interestInput)}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <Button onClick={save} disabled={saving}
            data-testid="profile-save-btn"
            className="w-full mt-6 h-11 rounded-full bg-[#D95D39] hover:bg-[#C24C29] font-semibold">
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </Card>
      </main>
    </div>
  );
}
